import dayjs from 'dayjs';
import { firestoreTimestampToDate } from 'utils/firestore-timestamp';

// Drops undefined / null / '' so an empty filter never reaches the API
// (axios would send `severity=` and the DTO's @IsEnum would 400).
export const pruneParams = params =>
    Object.fromEntries(
        Object.entries(params || {}).filter(
            ([, v]) => v !== undefined && v !== null && v !== ''
        )
    );

// Fixed vocab — mirrors sage-nest-api enums. Kept here so filter <Select>s
// stay populated even when the current page has none of a given value.
export const INCIDENT_SEVERITIES = ['info', 'minor', 'major', 'critical'];

export const INCIDENT_CATEGORIES = [
    'parking',
    'trespass',
    'suspicious',
    'noise',
    'disturbance',
    'damage',
    'theft',
    'amenity',
    'pet',
    'medical',
    'hazard',
    'maintenance',
    'other'
];

// Review-workflow vocab — live on every report filed after the manager-review
// rollout. Reports filed before then simply lack these fields until the
// one-off backfill script (scripts/firestore/backfill-shift-reporting-review-fields)
// stamps them.
export const INCIDENT_STATUSES = ['under_review', 'acknowledged', 'closed'];
export const INCIDENT_REVIEW_STAGES = ['supervisor', 'manager'];

// Mirrors sage-nest-api's IncidentTimeObserved enum.
export const INCIDENT_TIME_OBSERVED = ['happening_now', 'earlier'];

// The only party role a violation may attach to is `subject`, but any party
// with a `user_id` (subject, complainant, victim) is a valid pick — mirrors
// sage-nest-api's IncidentPartyRole enum.
export const INCIDENT_PARTY_SUBJECT_ROLE = 'subject';

// Supervisor-edit vocab for the parties/vehicles/witnesses lists — values
// verbatim from sage-nest-api's incident-party-role.enum.ts,
// incident-party-source.enum.ts and incident-vehicle-source.enum.ts.
export const INCIDENT_PARTY_ROLES = ['subject', 'complainant', 'victim'];
export const INCIDENT_PARTY_SOURCES = ['directory', 'unknown'];
export const INCIDENT_VEHICLE_SOURCES = ['lpr', 'manual'];

// Mirrors sage-nest-api's INCIDENT_CATEGORY_SUBTYPES (incident-subtype.enum.ts).
// Values verbatim — keep in sync if the API adds a category or sub-type.
export const INCIDENT_CATEGORY_SUBTYPES = {
    parking: [
        'parked_in_fire_lane',
        'blocked_driveway_or_hydrant',
        'no_permit_unauthorized_area',
        'abandoned_vehicle',
        'speeding',
        'reckless_driving',
        'gate_tailgating',
        'vehicle_accident',
        'parking_other'
    ],
    trespass: [
        'unauthorized_entry',
        'refused_to_leave',
        'fence_jumped_followed_vehicle_in',
        'after_hours_presence',
        'trespass_other'
    ],
    suspicious: [
        'loitering',
        'prowling_looking_into_homes_or_vehicles',
        'door_to_door_solicitation',
        'photographing_property',
        'suspicious_other'
    ],
    noise: [
        'loud_party_or_music',
        'construction_outside_permitted_hours',
        'vehicle_noise',
        'alarm_sounding',
        'noise_other'
    ],
    disturbance: [
        'verbal_altercation',
        'physical_altercation',
        'harassment_or_threats',
        'domestic_disturbance',
        'disturbance_other'
    ],
    damage: [
        'broken_glass_or_window',
        'vandalism_graffiti',
        'gate_or_barrier_strike',
        'landscape_or_turf_damage',
        'damage_other'
    ],
    theft: [
        'burglary_break_in',
        'stolen_property',
        'package_or_mail_theft',
        'attempted_theft',
        'theft_other'
    ],
    amenity: [
        'pool_use_after_hours',
        'glass_containers_at_pool',
        'non_resident_using_amenities',
        'unaccompanied_minors',
        'clubhouse_or_gym_misuse',
        'amenity_other'
    ],
    pet: [
        'dog_off_leash',
        'pet_waste_violation',
        'aggressive_animal',
        'stray_or_loose_animal',
        'wildlife_concern',
        'pet_other'
    ],
    medical: [
        'injury_or_fall',
        'medical_assist_rendered',
        'ems_fire_response_on_site',
        'medical_other'
    ],
    hazard: [
        'fire_or_smoke',
        'gas_odor',
        'downed_power_line',
        'flooding_major_leak',
        'slip_or_trip_hazard',
        'hazard_other'
    ],
    maintenance: [
        'street_light_out',
        'gate_or_barrier_malfunction',
        'irrigation_leak_or_overspray',
        'road_or_sidewalk_damage',
        'fence_or_wall_damage',
        'maintenance_other'
    ],
    other: ['policy_violation', 'lost_and_found', 'alarm_response', 'other_other']
};

// Curated display labels for category/sub_type/severity — sourced verbatim from the guard app's
// own copy (GC_IR_CATS/GC_SEV in the guard-companion mockup), not derived mechanically. Mirrors
// sage-nest-api's INCIDENT_CATEGORY_LABELS/INCIDENT_SUBTYPE_LABELS/INCIDENT_SEVERITY_LABELS
// (incident-report-labels.ts) — keep both in sync if the guard app's copy changes.
export const INCIDENT_CATEGORY_LABELS = {
    parking: 'Parking & vehicle',
    trespass: 'Trespassing',
    suspicious: 'Suspicious activity',
    noise: 'Noise',
    disturbance: 'Disturbance',
    damage: 'Property damage',
    theft: 'Theft & break-in',
    amenity: 'Amenity violation',
    pet: 'Pet & animal',
    medical: 'Medical',
    hazard: 'Safety hazard',
    maintenance: 'Maintenance',
    other: 'Other'
};

export const INCIDENT_SUBTYPE_LABELS = {
    // parking
    parked_in_fire_lane: 'Parked in fire lane',
    blocked_driveway_or_hydrant: 'Blocked driveway or hydrant',
    no_permit_unauthorized_area: 'No permit / unauthorized area',
    abandoned_vehicle: 'Abandoned vehicle',
    speeding: 'Speeding',
    reckless_driving: 'Reckless driving',
    gate_tailgating: 'Gate tailgating',
    vehicle_accident: 'Vehicle accident',
    parking_other: 'Other',

    // trespass
    unauthorized_entry: 'Unauthorized entry',
    refused_to_leave: 'Refused to leave',
    fence_jumped_followed_vehicle_in: 'Fence jumped / followed vehicle in',
    after_hours_presence: 'After-hours presence',
    trespass_other: 'Other',

    // suspicious
    loitering: 'Loitering',
    prowling_looking_into_homes_or_vehicles: 'Prowling / looking into homes or vehicles',
    door_to_door_solicitation: 'Door-to-door solicitation',
    photographing_property: 'Photographing property',
    suspicious_other: 'Other',

    // noise
    loud_party_or_music: 'Loud party or music',
    construction_outside_permitted_hours: 'Construction outside permitted hours',
    vehicle_noise: 'Vehicle noise',
    alarm_sounding: 'Alarm sounding',
    noise_other: 'Other',

    // disturbance
    verbal_altercation: 'Verbal altercation',
    physical_altercation: 'Physical altercation',
    harassment_or_threats: 'Harassment or threats',
    domestic_disturbance: 'Domestic disturbance',
    disturbance_other: 'Other',

    // damage
    broken_glass_or_window: 'Broken glass or window',
    vandalism_graffiti: 'Vandalism / graffiti',
    gate_or_barrier_strike: 'Gate or barrier strike',
    landscape_or_turf_damage: 'Landscape or turf damage',
    damage_other: 'Other',

    // theft
    burglary_break_in: 'Burglary / break-in',
    stolen_property: 'Stolen property',
    package_or_mail_theft: 'Package or mail theft',
    attempted_theft: 'Attempted theft',
    theft_other: 'Other',

    // amenity
    pool_use_after_hours: 'Pool use after hours',
    glass_containers_at_pool: 'Glass containers at pool',
    non_resident_using_amenities: 'Non-resident using amenities',
    unaccompanied_minors: 'Unaccompanied minors',
    clubhouse_or_gym_misuse: 'Clubhouse or gym misuse',
    amenity_other: 'Other',

    // pet
    dog_off_leash: 'Dog off leash',
    pet_waste_violation: 'Pet waste violation',
    aggressive_animal: 'Aggressive animal',
    stray_or_loose_animal: 'Stray or loose animal',
    wildlife_concern: 'Wildlife concern',
    pet_other: 'Other',

    // medical
    injury_or_fall: 'Injury or fall',
    medical_assist_rendered: 'Medical assist rendered',
    ems_fire_response_on_site: 'EMS / fire response on site',
    medical_other: 'Other',

    // hazard
    fire_or_smoke: 'Fire or smoke',
    gas_odor: 'Gas odor',
    downed_power_line: 'Downed power line',
    flooding_major_leak: 'Flooding / major leak',
    slip_or_trip_hazard: 'Slip or trip hazard',
    hazard_other: 'Other',

    // maintenance
    street_light_out: 'Street light out',
    gate_or_barrier_malfunction: 'Gate or barrier malfunction',
    irrigation_leak_or_overspray: 'Irrigation leak or overspray',
    road_or_sidewalk_damage: 'Road or sidewalk damage',
    fence_or_wall_damage: 'Fence or wall damage',
    maintenance_other: 'Other',

    // other
    policy_violation: 'Policy violation',
    lost_and_found: 'Lost and found',
    alarm_response: 'Alarm response',
    other_other: 'Other'
};

export const INCIDENT_SEVERITY_LABELS = {
    info: 'Informational',
    minor: 'Minor',
    major: 'Major',
    critical: 'Critical'
};

export const humanize = value =>
    typeof value === 'string' && value.length
        ? value.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())
        : '—';

// Fallback to humanize() is only for a value that no longer matches the current enum (a legacy
// record after a category/sub-type/severity was renamed) — every current value has a curated
// label above.
export const incidentCategoryLabel = value =>
    (value && INCIDENT_CATEGORY_LABELS[value]) || humanize(value);
export const incidentSubtypeLabel = value =>
    (value && INCIDENT_SUBTYPE_LABELS[value]) || humanize(value);
export const incidentSeverityLabel = value =>
    (value && INCIDENT_SEVERITY_LABELS[value]) || humanize(value);

// The sub-type as a reader should see it, free text included. sub_type_other is set exactly when
// sub_type ends with `_other`, and every `*_other` label is the bare word "Other" — so without
// the guard's own words the sub-type says nothing. Appends rather than replaces, matching
// sage-nest-api's incidentSubtypeDetail(), so the dialog reads the same as the exported PDF.
export const incidentSubtypeDetail = (value, other) => {
    const label = incidentSubtypeLabel(value);
    const trimmed = (other || '').trim();

    return trimmed ? `${label} — ${trimmed}` : label;
};

// This file is a hand-maintained mirror of sage-nest-api's incident-report-labels.ts, where
// `Record<Enum, string>` makes a missing label a compile error. Plain JS gets no such guarantee,
// so this is the closest equivalent: on every dev boot, assert that each value in the vocab
// lists above actually has a curated label. It catches the realistic drift — a sub-type added to
// INCIDENT_CATEGORY_SUBTYPES (or a category/severity added to its list) without a matching label
// — which would otherwise surface as a silently humanize()d cell in the inbox. Dev only; the
// production bundle drops the whole block.
if (process.env.NODE_ENV === 'development') {
    const missing = [
        ...INCIDENT_CATEGORIES.filter(v => !INCIDENT_CATEGORY_LABELS[v]).map(
            v => `category '${v}'`
        ),
        ...INCIDENT_SEVERITIES.filter(v => !INCIDENT_SEVERITY_LABELS[v]).map(
            v => `severity '${v}'`
        ),
        ...Object.values(INCIDENT_CATEGORY_SUBTYPES)
            .flat()
            .filter(v => !INCIDENT_SUBTYPE_LABELS[v])
            .map(v => `sub_type '${v}'`)
    ];

    if (missing.length) {
        // eslint-disable-next-line no-console
        console.error(
            `[guard-report.helper] Missing curated labels for ${missing.join(', ')}. ` +
                'Add them here and in sage-nest-api/src/incident-report/incident-report-labels.ts.'
        );
    }
}

export const severityColor = severity => {
    switch (severity) {
        case 'critical':
            return 'error';
        case 'major':
            return 'warning';
        case 'minor':
            return 'info';
        case 'info':
        default:
            return 'default';
    }
};

export const statusColor = status => {
    switch (status) {
        case 'under_review':
            return 'info';
        case 'acknowledged':
            return 'warning';
        case 'closed':
            return 'default';
        default:
            return 'default';
    }
};

// DAR merged-timeline row `type`: the six guard note types plus three auto values.
export const darRowColor = type => {
    switch (type) {
        case 'admitted':
            return 'success';
        case 'denied':
            return 'error';
        case 'incident_filed':
            return 'warning';
        default:
            return 'default';
    }
};

export const certifiedLabel = certified => (certified ? 'Certified' : 'In progress');
export const certifiedColor = certified => (certified ? 'success' : 'default');

export const formatDate = ts => {
    const d = firestoreTimestampToDate(ts);
    return d ? dayjs(d).format('MMM D, YYYY') : '—';
};

export const formatDateTime = ts => {
    const d = firestoreTimestampToDate(ts);
    return d ? dayjs(d).format('MMM D, YYYY h:mm A') : '—';
};

export const errorMessageFrom = error => {
    const message = error?.response?.data?.message ?? error?.message ?? error;
    return Array.isArray(message) ? message.join(', ') : String(message);
};
